import React, { useState, useEffect } from 'react';
import { CardHeader, CardContent, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { validate as uuidValidate } from 'uuid';
import {
  getFlagStatus,
  getFlagStatusBulk,
  saveFeatureBulk,
  removeFeatureBulk,
} from '../../core/features';

import {
  StyledCard,
  ResponseText,
  StatusLabel,
  StyledInput,
  StyledButton,
  BulkCourseContainer,
} from './FeatureFormStyles';

function FeatureForm({ feature, flaggingType }) {
  const [statusId, setStatusId] = useState('');
  const [bulkIds, setBulkIds] = useState('');
  const [response, setResponse] = useState(null);
  const [featureIds, setFeatureIds] = useState([]);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    fetchFeatureRecords(flaggingType, feature);
  }, [flaggingType, feature]);

  const fetchFeatureRecords = async (flaggingType, nextFeature) => {
    const response = await getFlagStatusBulk(flaggingType, nextFeature);
    setFeatureIds(response);
  };

  const handleStatusChange = (event) => {
    setStatusId(event.target.value);
  };

  const isValidId = (id) => {
    if (flaggingType === 'course') {
      if (!uuidValidate(id)) {
        setResponse({ message: `Invalid ${flaggingType} id: ${id}` });
        return false;
      }
    }
    return true;
  };

  const checkStatus = async () => {
    if (!isValidId(statusId)) {
      return;
    }

    const response = await getFlagStatus(statusId, flaggingType, feature);
    setResponse({ message: `isEnabled: ${response && response.isEnabled}` });
  };

  const handleBulkIdsChange = (event) => {
    setBulkIds(event.target.value);
  };

  const addBulkIds = async () => {
    const flaggingIds = bulkIds.replace(/\n/g, '').split(',');

    if (!flaggingIds.every(isValidId)) {
      return;
    }

    const response = await saveFeatureBulk(flaggingType, flaggingIds, feature);
    fetchFeatureRecords(flaggingType, feature);
    setResponse(response);
  };

  const removeBulkIds = async () => {
    const flaggingIds = bulkIds.replace(/\n/g, '').split(',');

    if (!flaggingIds.every(isValidId)) {
      return;
    }

    const response = await removeFeatureBulk(
      flaggingType,
      flaggingIds,
      feature,
    );
    fetchFeatureRecords(flaggingType, feature);
    setResponse(response);
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const getDisplayValue = (item) => {
    return flaggingType === 'course' ? item.course_id : item.isbn;
  };

  return (
    <StyledCard>
      <CardHeader
        title={`Manage by ${flaggingType}`}
        action={
          <IconButton
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="show more"
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            }}
            size="large"
          >
            <ExpandMoreIcon />
          </IconButton>
        }
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          <ResponseText data-test-id="response">
            {response && response.message}
          </ResponseText>
          <div>
            <StatusLabel>Check {flaggingType} Status</StatusLabel>
            <StyledInput
              type="text"
              size="50"
              data-test-id="status-course-id-input"
              onChange={(e) => handleStatusChange(e)}
            />
            <StyledButton
              data-test-id="status-submit-button"
              onClick={checkStatus}
              disabled={!statusId}
            >
              Submit
            </StyledButton>
          </div>
          <BulkCourseContainer>
            <textarea
              name="bulkFeatureCourse"
              placeholder={`Enter ${flaggingType} ids separated by commas`}
              data-test-id="bulk-course-ids-input"
              rows={10}
              cols={50}
              onChange={(e) => {
                handleBulkIdsChange(e);
              }}
            />
            <div>
              <StyledButton
                data-test-id="bulk-remove-button"
                onClick={removeBulkIds}
                disabled={!bulkIds}
              >
                Remove
              </StyledButton>
              <StyledButton
                data-test-id="bulk-add-button"
                onClick={addBulkIds}
                disabled={!bulkIds}
              >
                Add
              </StyledButton>
            </div>
          </BulkCourseContainer>
          <div>
            <h2>Enabled {flaggingType}s</h2>
            {(Array.isArray(featureIds) &&
              featureIds.map((item) => (
                <div key={item.id}>{getDisplayValue(item)}</div>
              ))) || <div>No {flaggingType}s enabled</div>}
          </div>
        </CardContent>
      </Collapse>
    </StyledCard>
  );
}

export default FeatureForm;
